import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';

// ============================================
// 공통 모달
// ============================================
export const TemplateModalOverlay = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const ModalHeader = styled(Box)({
  backgroundColor: '#111',
  height: '52px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderTopRightRadius: '10px',
  borderTopLeftRadius: '10px',
  padding: '0 20px',
  flexShrink: 0,
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
});

export const ModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '10px',
  padding: '14px 20px',
  borderTop: '1px solid #eee',
  backgroundColor: '#fafafa',
});

// ============================================
// 템플릿 관리 모달
// ============================================
export const ManageContainer = styled(TemplateModalOverlay)({
  width: '850px',
  maxHeight: '85vh',
});

export const ManageContent = styled(Box)({
  flex: 1,
  minHeight: '450px',
  padding: '20px',
  overflowY: 'auto',
});

export const TemplateTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
});

export const TableHead = styled('thead')({
  '& th': {
    padding: '10px 12px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#555',
    borderBottom: '2px solid #4AB3BC',
    borderRight: '1px solid #e0e0e0',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    backgroundColor: '#f0f8f8',
    '&:last-child': { borderRight: 'none' },
  },
});

export const TableBody = styled('tbody')({
  '& td': {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
    borderRight: '1px solid #f0f0f0',
    textAlign: 'center',
    '&:last-child': { borderRight: 'none' },
  },
  '& tr:hover': {
    backgroundColor: '#f8f8f8',
  },
});

export const ActionButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'buttonColor',
})(({ buttonColor }) => ({
  minWidth: '40px',
  height: '26px',
  fontSize: '12px',
  fontWeight: 500,
  borderRadius: '4px',
  padding: '0 8px',
  ...(buttonColor === 'edit' && {
    border: '1px solid #4AB3BC',
    color: '#4AB3BC',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'rgba(74, 179, 188, 0.08)' },
  }),
  ...(buttonColor === 'delete' && {
    border: '1px solid #ef5350',
    color: '#ef5350',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'rgba(239, 83, 80, 0.08)' },
  }),
}));

export const EmptyMessage = styled(Typography)({
  textAlign: 'center',
  fontSize: '14px',
  color: '#999',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: '300px',
});

// ============================================
// 생성/수정 모달 (폼 전용)
// ============================================
export const CreateContainer = styled(TemplateModalOverlay)({
  width: '580px',
});

export const CreateContent = styled(Box)({
  padding: '24px 28px',
});

export const FormRow = styled(Box)({
  display: 'flex',
  gap: '14px',
  marginBottom: '18px',
  '&:last-child': { marginBottom: 0 },
});

export const FormField = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const FormFieldLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
});

// ============================================
// 연결된 스케줄 모달
// ============================================
export const EditContainer = styled(TemplateModalOverlay)({
  width: '1100px',
  height: '80vh',
  maxHeight: '90vh',
});

export const EditContent = styled(Box)({
  flex: 1,
  minHeight: 0,
  padding: '20px 24px',
  overflowY: 'auto',
});

export const TemplateSummaryCard = styled(Box)({
  display: 'flex',
  gap: '20px',
  padding: '14px 18px',
  backgroundColor: '#f7fafa',
  border: '1px solid #e0eded',
  borderRadius: '8px',
  marginBottom: '18px',
});

export const SummaryItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

export const SummaryLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 500,
  color: '#888',
});

export const SummaryValue = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
});

export const AppliedLessonsHeader = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  marginBottom: '10px',
});

export const AppliedLessonsTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
});

export const AppliedLessonsNote = styled(Typography)({
  fontSize: '12px',
  color: '#c44',
});

export const AppliedLessonsTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  '& thead th': {
    padding: '9px 10px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#555',
    borderBottom: '2px solid #4AB3BC',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    backgroundColor: '#f0f8f8',
  },
  '& tbody td': {
    padding: '9px 10px',
    fontSize: '13px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
    textAlign: 'center',
  },
  '& tbody tr:hover': {
    backgroundColor: '#f5fafa',
  },
});

// ============================================
// 새 일정 등록 모달
// ============================================
export const ScheduleCreateContainer = styled(TemplateModalOverlay)({
  width: '1100px',
  maxHeight: '90vh',
});

export const ScheduleCreateContent = styled(Box)({
  flex: 1,
  minHeight: 0,
  padding: '24px 28px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
});

export const SectionTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#333',
  marginBottom: '16px',
});

export const SettingGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '320px 1fr 1fr',
  gap: '0',
  '& > *': {
    padding: '0 20px',
  },
  '& > *:first-of-type': {
    paddingLeft: 0,
  },
  '& > *:last-child': {
    paddingRight: 0,
  },
  '& > *:not(:last-child)': {
    borderRight: '1px solid #ddd',
  },
});

export const SettingColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
});

export const FieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const FieldLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#555',
});

export const InfoText = styled(Typography)({
  fontSize: '13px',
  color: '#555',
});

export const DayButtonGroup = styled(Box)({
  display: 'flex',
  gap: '6px',
});

export const DayButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  minWidth: '40px',
  minHeight: '40px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  padding: 0,
  border: '1px solid #aaa',
  ...(isActive
    ? {
        backgroundColor: '#4AB3BC',
        color: '#fff',
        borderColor: '#4AB3BC',
        '&:hover': { backgroundColor: '#3da0a8' },
      }
    : {
        backgroundColor: '#fff',
        color: '#888',
        '&:hover': { backgroundColor: '#f5f5f5' },
      }),
}));

export const ScheduleSection = styled(Box)({
  marginTop: '28px',
  paddingTop: '20px',
  borderTop: '1px solid #e1e1e1',
});

export const ScheduleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px 16px',
  backgroundColor: '#fafafa',
  borderRadius: '8px',
  marginBottom: '8px',
});

export const ScheduleRowLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
  minWidth: '48px',
});

export const ScheduleActionButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'buttonVariant',
})(({ buttonVariant }) => ({
  minWidth: '48px',
  height: '28px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: 600,
  padding: '0 8px',
  ...(buttonVariant === 'add' && {
    backgroundColor: '#555',
    color: '#fff',
    '&:hover': { backgroundColor: '#444' },
  }),
  ...(buttonVariant === 'copy' && {
    backgroundColor: '#555',
    color: '#fff',
    '&:hover': { backgroundColor: '#444' },
  }),
  ...(buttonVariant === 'remove' && {
    backgroundColor: '#ef5350',
    color: '#fff',
    '&:hover': { backgroundColor: '#d32f2f' },
  }),
}));

export const ToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '24px',
  padding: '12px 0',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#555',
});

// ============================================
// 자동 공개&폐강 모달
// ============================================
export const AutoSettingContainer = styled(TemplateModalOverlay)({
  width: '480px',
});

export const AutoSettingContent = styled(Box)({
  padding: '24px',
});

export const AutoSettingSection = styled(Box)({
  marginBottom: '24px',
});

export const AutoSettingSectionTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#333',
  marginRight: '12px',
});

export const AutoSettingRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginTop: '12px',
});

export const AutoSettingLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#555',
});

// ============================================
// 설정하기 버튼 (공개&폐강)
// ============================================
export const SettingButton = styled(Button)({
  height: '36px',
  width: '200px',
  borderRadius: '6px',
  border: '1px solid #ddd',
  color: '#555',
  fontSize: '13px',
  fontWeight: 500,
  '&:hover': { backgroundColor: '#f8f8f8', borderColor: '#4AB3BC' },
});

export const StatusChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  height: '20px',
  minWidth: '32px',
  padding: '0 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  fontSize: '11px',
  fontWeight: 600,
  ...(active
    ? { backgroundColor: '#4AB3BC', color: '#fff' }
    : { backgroundColor: '#eee', color: '#bbb' }),
}));
